import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import {
  getPresetOptions,
  generateTimeSlots,
  applyTimeToDate,
  isToday,
  type SnoozeOption,
} from '@/lib/snoozeTimeUtils'

export type SnoozePickerMode = 'presets' | 'custom'

interface UseSnoozePickerOptions {
  onSnooze: (datetime: Date) => Promise<void>
  onComplete?: () => void
}

export interface UseSnoozePickerReturn {
  isOpen: boolean
  mode: SnoozePickerMode
  selectedPresetIndex: number
  selectedDate: Date
  selectedTimeIndex: number
  currentMonth: Date
  presetOptions: SnoozeOption[]
  timeSlots: string[]
  open: () => void
  close: () => void
  setMode: (mode: SnoozePickerMode) => void
  selectPreset: (index: number) => void
  selectDate: (date: Date) => void
  selectTime: (index: number) => void
  navigateMonth: (direction: -1 | 1) => void
  confirmPreset: () => Promise<void>
  confirmCustom: () => Promise<void>
  isLoading: boolean
}

// Index of 09:00 in the full 48-slot array (0:00, 0:30, ..., 09:00 = index 18)
const DEFAULT_TIME_INDEX_9AM = 18

/**
 * Compute the index of the next available 30-minute slot after the current time.
 * Each hour has 2 slots (on the hour and at :30), so index = hour * 2 + offset.
 */
function getNextSlotIndex(now: Date): number {
  const hour = now.getHours()
  const minutes = now.getMinutes()

  if (minutes < 30) {
    // Current time is before :30, so the next slot is :30 of this hour
    return hour * 2 + 1
  }
  // Current time is :30 or later, so the next slot is :00 of the next hour
  return (hour + 1) * 2
}

export function useSnoozePicker({
  onSnooze,
  onComplete,
}: UseSnoozePickerOptions): UseSnoozePickerReturn {
  const [isOpen, setIsOpen] = useState(false)
  const [mode, setMode] = useState<SnoozePickerMode>('presets')
  const [selectedPresetIndex, setSelectedPresetIndex] = useState(0)
  const [selectedDate, setSelectedDate] = useState(() => new Date())
  const [selectedTimeIndex, setSelectedTimeIndex] = useState(0)
  const [currentMonth, setCurrentMonth] = useState(() => new Date())
  const [isLoading, setIsLoading] = useState(false)

  const presetOptions = getPresetOptions()
  const allTimeSlots = useMemo(() => generateTimeSlots(), [])
  const isSelectedDateToday = isToday(selectedDate)

  // Filter time slots: only show future slots when the selected date is today
  const timeSlots = useMemo(() => {
    if (!isSelectedDateToday) return allTimeSlots

    const nextIndex = getNextSlotIndex(new Date())
    return allTimeSlots.slice(nextIndex)
  }, [isSelectedDateToday, allTimeSlots])

  // Reset time index when switching between today and a future date
  useEffect(() => {
    if (isSelectedDateToday) {
      setSelectedTimeIndex(0)
    } else {
      setSelectedTimeIndex(DEFAULT_TIME_INDEX_9AM)
    }
  }, [isSelectedDateToday])

  // Shared snooze execution to avoid duplicating try/catch/loading logic
  const executeSnooze = useCallback(
    async (datetime: Date) => {
      if (isLoading) return
      setIsLoading(true)
      try {
        await onSnooze(datetime)
        setIsOpen(false)
        setIsLoading(false)
        onComplete?.()
      } catch (error) {
        console.error('Failed to snooze:', error)
        setIsLoading(false)
      }
    },
    [isLoading, onSnooze, onComplete]
  )

  // Use refs to avoid stale closures in the keyboard effect
  const stateRef = useRef({
    presetOptions,
    selectedPresetIndex,
    selectedDate,
    selectedTimeIndex,
    timeSlots,
  })
  stateRef.current = {
    presetOptions,
    selectedPresetIndex,
    selectedDate,
    selectedTimeIndex,
    timeSlots,
  }

  const open = useCallback(() => {
    setIsOpen(true)
    setMode('presets')
    setSelectedPresetIndex(0)
    setSelectedDate(new Date())
    setSelectedTimeIndex(0)
    setCurrentMonth(new Date())
    setIsLoading(false)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
    setIsLoading(false)
  }, [])

  const navigateMonth = useCallback((direction: -1 | 1) => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev)
      newMonth.setMonth(newMonth.getMonth() + direction)
      return newMonth
    })
  }, [])

  const confirmPreset = useCallback(async () => {
    const option = presetOptions[selectedPresetIndex]
    if (option) await executeSnooze(option.datetime)
  }, [presetOptions, selectedPresetIndex, executeSnooze])

  const confirmCustom = useCallback(async () => {
    const timeString = timeSlots[selectedTimeIndex]
    const datetime = applyTimeToDate(selectedDate, timeString)
    await executeSnooze(datetime)
  }, [timeSlots, selectedTimeIndex, selectedDate, executeSnooze])

  // Keyboard navigation via global listener
  useEffect(() => {
    if (!isOpen) return

    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault()
        if (mode === 'custom') {
          setMode('presets')
        } else {
          close()
        }
        return
      }

      if (mode === 'presets') {
        handlePresetsKeyDown(e)
      } else if (mode === 'custom') {
        handleCustomKeyDown(e)
      }
    }

    function handlePresetsKeyDown(e: KeyboardEvent): void {
      const { presetOptions: opts, selectedPresetIndex: idx } = stateRef.current

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedPresetIndex((prev) =>
            prev < opts.length ? prev + 1 : prev
          )
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedPresetIndex((prev) => (prev > 0 ? prev - 1 : 0))
          break
        case 'Enter':
          e.preventDefault()
          if (idx === opts.length) {
            setMode('custom')
          } else {
            const option = opts[idx]
            if (option) executeSnooze(option.datetime)
          }
          break
      }
    }

    function handleCustomKeyDown(e: KeyboardEvent): void {
      function shiftDate(days: number): void {
        setSelectedDate((prev) => {
          const newDate = new Date(prev)
          newDate.setDate(newDate.getDate() + days)
          return newDate
        })
      }

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault()
          shiftDate(-1)
          break
        case 'ArrowRight':
          e.preventDefault()
          shiftDate(1)
          break
        case 'ArrowUp':
          e.preventDefault()
          shiftDate(-7)
          break
        case 'ArrowDown':
          e.preventDefault()
          shiftDate(7)
          break
        case 'Enter': {
          e.preventDefault()
          const { timeSlots: slots, selectedTimeIndex: tIdx, selectedDate: date } = stateRef.current
          const timeString = slots[tIdx]
          const datetime = applyTimeToDate(date, timeString)
          executeSnooze(datetime)
          break
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, mode, close, executeSnooze])

  return {
    isOpen,
    mode,
    selectedPresetIndex,
    selectedDate,
    selectedTimeIndex,
    currentMonth,
    presetOptions,
    timeSlots,
    open,
    close,
    setMode,
    selectPreset: setSelectedPresetIndex,
    selectDate: setSelectedDate,
    selectTime: setSelectedTimeIndex,
    navigateMonth,
    confirmPreset,
    confirmCustom,
    isLoading,
  }
}
